import {format} from 'date-fns/format'
import {isValid} from 'date-fns/isValid'
import {parseISO} from 'date-fns/parseISO'
import memoizeOne from 'memoize-one'
import React from 'react'
export {getString} from '@atomic.io/common/types/helpers'

export const toTitleCase = (str: string) =>
  str.toLowerCase().replace(/\w\S*/g, upperCaseFirst)

export const camelToReadable = (s: string): string => {
  const result = s.replace(/([A-Z])/g, ' $1').toLowerCase()
  return upperCaseFirst(result)
}

export const upperCaseFirst = (str: string) =>
  str.charAt(0).toUpperCase() + str.slice(1)

export const hasValue = (str?: string | null | undefined): boolean =>
  (str?.trim().length ?? 0) > 0

// matches private.email in database
export const EMAIL_REGEX =
  /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/

export const isEmptyObject = (obj: unknown) =>
  obj &&
  typeof obj === 'object' &&
  Object.keys(obj).length === 0 &&
  obj.constructor === Object

export const trimAndLimit = (limit: number, str: string) => {
  const source = str.trim()
  if (source.length <= limit) return source
  const trimmed = source.slice(0, Math.max(0, limit)).trim()
  if (trimmed.length > 3) return trimmed.slice(0, -3) + '...'
  return trimmed
}

export const makePageTitle = (title: string, scope?: string): string => {
  const charLimit = 20 // useful when e.g. a card title is really large
  return (scope ? `${trimAndLimit(charLimit, scope)} ` : '') + `(${title})`
}

export const uniqueBy = <T, P extends string | number | boolean>(
  items: T[] | undefined,
  by: (item: T) => P
): T[] | undefined =>
  items ? [...new Map(items.map(v => [by ? by(v) : v, v])).values()] : undefined

export const unique = <T extends string | number | boolean>(
  items: T[] | undefined
): T[] | undefined => uniqueBy(items, v => v)

export const coerceString = (value: unknown) => {
  if (value === undefined || value === null) return undefined
  return String(value)
}

export const toRelativeURL = (url: string) => {
  const urlCopy = new URL(url)
  return urlCopy
    .toString()
    .replace(/^https?:\/\//, '')
    .replace(urlCopy.host, '')
}

export const displayDate = (timestamp: string, withTime = true) => {
  const date = parseISO(timestamp)
  return isValid(date)
    ? format(
        parseISO(timestamp),
        `${withTime ? 'h:mm a, ' : ''}EEE d MMM, yyyy`
      )
    : null
}

export const pruneObject = memoizeOne(
  <T extends Record<string, unknown>>(
    obj: T,
    emptyValues: unknown[] = [null, undefined]
  ): T => {
    const mut_newObj = {...obj}
    for (const [key, value] of Object.entries(mut_newObj)) {
      if (emptyValues.includes(value)) {
        delete mut_newObj[key]
      }
    }
    return mut_newObj
  }
)

//Returns an array containing elements that are in array1 but not in array2.
export const arrayDiff = <T>(array1: T[], array2: T[]): T[] => {
  // Create a Set from array2 for faster membership checking
  const set2 = new Set(array2)

  // Use filter to return elements that are in array1 but not in array2
  return array1.filter(element => !set2.has(element))
}

export const debounce = <A extends any[]>(
  fn: (...args: A) => void,
  delay: number
) => {
  let timeoutId: NodeJS.Timeout | null = null
  return (...args: A): void => {
    if (timeoutId) {
      clearTimeout(timeoutId)
    }
    timeoutId = setTimeout(() => fn(...args), delay)
  }
}
/**
 * Strip formatting from pasted text, useful for contenteditable divs
 *
 * https://developer.mozilla.org/en-US/docs/Web/API/Element/paste_event#javascript
 */
export const pasteAsText = (event: React.ClipboardEvent<HTMLDivElement>) => {
  const paste = event.clipboardData.getData('text/plain')
  const selection = window.getSelection()
  if (!selection?.rangeCount) return

  event.preventDefault()
  selection.deleteFromDocument()
  selection.getRangeAt(0).insertNode(document.createTextNode(paste))
  selection.collapseToEnd()
}

// mergeRefs lifted from https://github.com/gregberge/react-merge-refs/blob/main/src/index.ts
function assignRef<T>(
  ref: React.Ref<T> | undefined | null,
  value: T | null
): ReturnType<React.RefCallback<T>> {
  if (typeof ref === 'function') {
    return ref(value)
  } else if (ref) {
    ref.current = value
  }
}

export function mergeRefs<T>(refs: (React.Ref<T> | undefined)[]): React.Ref<T> {
  return (value: T | null) => {
    const cleanups: (() => void)[] = []

    for (const ref of refs) {
      const cleanup = assignRef(ref, value)
      const isCleanup = typeof cleanup === 'function'
      cleanups.push(isCleanup ? cleanup : () => assignRef(ref, null))
    }

    return () => {
      for (const cleanup of cleanups) cleanup()
    }
  }
}

// replace character not suitable as filenames for given timezone
// e.g.  America/Port-au-Prince -> America_Port-au-Prince
// e.g. Pacific/Pago_pago -> Pacific_Pago_pago
// e.g. Etc/GMT+9 -> Etc_GMT_9
export const normaliseTimezone = (timezoneString: string) => {
  /**
   * America/Port-au-Prince -> America_Port-au-Prince
   * Pacific/Pago_pago -> Pacific_Pago_pago
   * Etc/GMT+9 -> Etc_GMT_9
   */
  return timezoneString
    .replaceAll(/[/+]/g, '_')
    .replace(/^([^a-z0-9:_-]|\s)+$/gi, '')
}

const numberWords = [
  'zero',
  'one',
  'two',
  'three',
  'four',
  'five',
  'six',
  'seven',
  'eight',
  'nine',
  'ten'
]
export const numberToWords = (num: number): string => {
  return num < numberWords.length ? numberWords[num] : num.toLocaleString()
}

export const isURLWithCustomPort = (input: string | undefined): boolean => {
  if (!input) {
    return false
  }

  try {
    const port = URL.parse(input)?.port

    // check non standard http/https ports
    return !!port?.length && port !== '443' && port !== '80'
  } catch (err) {
    // ignore
  }

  return false
}

export const mapValues = <T, U>(
  fn: (v: T) => U,
  obj: Record<string, T>
): Record<string, U> =>
  Object.fromEntries(Object.entries(obj).map(([k, v]) => [k, fn(v)]))

export const groupBy = <T, K extends PropertyKey>(
  deriveKey: (item: T) => K,
  ls: T[]
): Record<string, T[]> =>
  ls.reduce(
    (mut_grouped, item) => {
      const key = deriveKey(item)
      if (mut_grouped[key]) mut_grouped[key].push(item)
      else mut_grouped[key] = [item]
      return mut_grouped
    },
    {} as Record<K, T[]>
  )
